import React, { useMemo } from "react";

import { Tooltip } from "@mui/material";
import { Divider, Grid } from "@mui/material";
import { differenceInDays, startOfDay, startOfToday } from "date-fns";
import { AppListItem } from "Components/AppListItem";
import { useDespatchActions } from "../../views/DiaryResourcesView/hooks/useDespatchActions";
import AutoSuggest from "Components/Form/AutoSuggest";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";
import { DespatchTask } from "Models/despatch.task";
import { DespatchCrew } from "Models/despatch.crew";
import { Member } from "Models/members/member";
import { StorageBox } from "Models/storage.box";
import { useTranslation } from "react-i18next";
import useMeasure from "react-use-measure";
import { getEquipmentName } from "Util/getEquipmentName";
import { getStorageBoxName } from "Util/getStorageBoxName";
import { useAppState } from "Context/StateContext";
import { getCompleteDateRangeLabel } from "Util/startDate";

import { Fleet } from "../../../../../../models/fleet";
import { Job } from "../../../../../../models/job";
import { User } from "../../../../../../models/user";
import {
  getOrderCustomerDisplayName,
  getStaffFullname,
  getUserFullname,
} from "../../../../../../util/names";
import {
  attributeIsLeader,
  compareIds,
  RenderBadgeArgs,
  SeeTaskContentProps,
  TaskContentAssignField,
  TaskContentSections,
} from "../types";
import useStyles from "./styles";

export const SeeTaskContent: React.FC<SeeTaskContentProps> = (props) => {
  const { task, wrapLines = true, onEdit, canEdit, editPermission } = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { getMembersList, getFleetList, getStorageBoxes } = useDespatchActions();

  const { data: memberList } = getMembersList();
  const { data: fleetList } = getFleetList();
  const { data: storageBoxesList } = getStorageBoxes();

  const leaderList = useMemo(
    () => memberList.filter((member) => member.userCompany?.leader),
    [memberList]
  );

  const [tagContainerRef, tagContainerBounds] = useMeasure();

  const dateRanged = useMemo(() => {
    return getCompleteDateRangeLabel({
      startDate: task.startDate,
      endDate: task.endDate,
      separator: "-",
      preferences: currentUser?.preferences,
    });
  }, [task, currentUser?.preferences]);

  const checkAssignAvailableByAttribute = (attribute?: string) => {
    return (
      canEdit &&
      editPermission &&
      attribute &&
      // eslint-disable-next-line no-prototype-builtins
      editPermission.hasOwnProperty(attribute) &&
      editPermission[attribute as keyof typeof editPermission]
    );
  };

  const fields = useMemo(() => {
    const team =
      task.team ??
      task.teams?.find((t) => {
        const createdTo = new Date(t.createdTo!);
        return differenceInDays(startOfToday(), startOfDay(createdTo)) === 0;
      });
    const fleets = team?.fleets ?? [];
    const members = team?.members ?? [];
    const storageBoxes = task.storageBoxes ?? [];

    const fleetValueLabel = (
      fleets
        .map((fleet) => {
          const fleetName = getEquipmentName(fleet).fullText;

          if (fleetName) return fleetName;

          return undefined;
        })
        .filter(Boolean) as string[]
    ).join(", ");

    const memberValueLabel = (
      members
        .map((member) => {
          const memberName = getStaffFullname(member);

          if (memberName) return memberName;

          return undefined;
        })
        .filter(Boolean) as string[]
    ).join(", ");

    const storageBoxValueLabel = (
      storageBoxes
        .map((storageBox) => {
          if (storageBox) return getStorageBoxName(storageBox).fullText;

          return undefined;
        })
        .filter(Boolean) as string[]
    ).join(", ");

    return {
      orderReference: {
        key: "orderReference",
        isMany: false,
        label: translate("orderReference"),
        value: task.job.reference,
      },
      customer: {
        key: "customer",
        isMany: false,
        label: translate("customer"),
        value: getOrderCustomerDisplayName(task.job),
      },
      tag: {
        key: "tag",
        isMany: true,
        label: translate("tag"),
        value: task.job.tag,
      },
      leader: {
        key: "leader",
        isMany: false,
        label: translate("leader"),
        value: checkAssignAvailableByAttribute("leader")
          ? team?.memberLeader
          : getUserFullname(team?.memberLeader),
        options: leaderList,
        renderLabelOption: (labelOption: User) => getUserFullname(labelOption),
        placeholder: translate("searchByLeader"),

        onChange(value) {
          const newTask = { ...task };
          const teamToUpdateIndex = newTask.teams?.findIndex((t) => t.id === team?.id)!;

          if (teamToUpdateIndex >= 0 && newTask.teams?.[teamToUpdateIndex]) {
            newTask.teams[teamToUpdateIndex] = {
              ...(team ?? {}),
              memberLeader: value,
            } as DespatchCrew;
          }

          onEdit?.(newTask);
        },
      },
      staffs: {
        key: "staffs",
        isMany: true,
        label: translate("members"),
        options: memberList,
        valueLabel: memberValueLabel,
        value: members,
        placeholder: translate("searchByStaff"),
        onChange(value) {
          const newTask = { ...task };
          const teamToUpdateIndex = newTask.teams?.findIndex((t) => t.id === team?.id)!;

          if (teamToUpdateIndex >= 0 && newTask.teams?.[teamToUpdateIndex]) {
            newTask.teams[teamToUpdateIndex] = { ...(team ?? {}), members: value } as DespatchCrew;
          }

          onEdit?.(newTask);
        },
        renderLabelOption: (labelOption: Member) => getStaffFullname(labelOption),
      },
      taskType: {
        key: "taskType",
        isMany: false,
        label: translate("task"),
        value: task.type,
        valueLabel: (
          <div className={classes.containerTaskStatus}>
            {task.type} <BadgeJobStatus value={task.status} className={classes.badgeTaskStatus} />
          </div>
        ),
      },
      time: {
        key: "time",
        isMany: false,
        label: translate("time"),
        value: dateRanged,
      },
      fleets: {
        key: "fleets",
        isMany: true,
        label: translate("equipments"),
        placeholder: translate("searchByFleet"),
        valueLabel: fleetValueLabel,
        value: fleets,
        options: fleetList,
        renderLabelOption: (labelOption: Fleet) => getEquipmentName(labelOption).fullText,
        onChange(value) {
          const newTask = { ...task };
          const teamToUpdateIndex = newTask.teams?.findIndex((t) => t.id === team?.id)!;

          if (teamToUpdateIndex >= 0 && newTask.teams?.[teamToUpdateIndex]) {
            newTask.teams[teamToUpdateIndex] = { ...(team ?? {}), fleets: value } as DespatchCrew;
          }

          onEdit?.(newTask);
        },
      },
      storageBoxes: {
        key: "storageBoxes",
        isMany: true,
        label: translate("storageBoxes"),
        placeholder: translate("searchByStorageBox"),
        value: storageBoxes,
        valueLabel: storageBoxValueLabel,
        options: storageBoxesList,
        renderLabelOption: (labelOption: StorageBox) => getStorageBoxName(labelOption).fullText,
        onChange(value) {
          const newTask = { ...task, job: { ...task.job }, storageBoxes: value };

          onEdit?.(newTask);
        },
      },
    } as TaskContentSections;
  }, [task, storageBoxesList, memberList, fleetList, leaderList, canEdit, editPermission]);

  const renderEmptySection = (title: string) => {
    return (
      <AppListItem
        title={title}
        subtitle={translate("noInformationToShow")}
        disableGutters
        disablePadding
        disableMargin
      />
    );
  };

  const renderInputInformation = (assignField: TaskContentAssignField) => {
    return (
      <AutoSuggest
        dataCy={`suggest-${assignField.key}`}
        label={assignField.label}
        placeholder={assignField.placeholder}
        getOptionLabel={assignField.renderLabelOption}
        inputValue={
          attributeIsLeader(assignField.key) ? getUserFullname(assignField.value as any) : undefined
        }
        getOptionSelected={compareIds}
        value={assignField.value}
        multiple={assignField.isMany}
        onChange={assignField.onChange}
        options={assignField.options}
      />
    );
  };

  const renderSimpleSection = (key: keyof TaskContentSections) => {
    const field = fields[key];

    if (!field) return null;

    if (checkAssignAvailableByAttribute(key) && "onChange" in field) {
      return renderInputInformation(field);
    }

    if (!field.value || (field.isMany && !(field.value as [])?.length))
      return renderEmptySection(field.label);

    return (
      <AppListItem
        title={field.label}
        subtitle={field.valueLabel ?? field.value}
        disableGutters
        disablePadding
        disableMargin
      />
    );
  };

  const renderBadges = (args: RenderBadgeArgs) => {
    const { title, values, boxBadgeWidth, boxContainerMax, className } = args;

    const { length } = values;

    if (!length) return renderEmptySection(title);

    let valuesDeleted: string[] = [];
    let deletedTooltip = "";

    const BOX_BADGE_WIDTH = boxBadgeWidth;
    const BOX_CONTAINER_MAX = boxContainerMax;
    const SIZE_ALL_BOXES = BOX_BADGE_WIDTH * length;

    if (SIZE_ALL_BOXES > BOX_CONTAINER_MAX) {
      const howMuch = Number.parseInt(String(BOX_CONTAINER_MAX / BOX_BADGE_WIDTH), 10);

      let exceeded = length - howMuch;

      if (exceeded > 0) {
        exceeded += 1;
        valuesDeleted = values.splice(length - exceeded, exceeded);
        deletedTooltip = valuesDeleted.join(", ");
      }
    }

    return (
      <AppListItem
        title={title}
        subtitle={
          <>
            {values.map((value, index) => (
              <Tooltip key={index} title={value}>
                <Grid item className={className}>
                  <span className={classes.simpleBadgeText}>{value}</span>
                </Grid>
              </Tooltip>
            ))}
            {valuesDeleted.length > 0 && (
              <Tooltip title={deletedTooltip}>
                <Grid item className={className}>
                  <span className={classes.simpleBadgeText}>{valuesDeleted.length} more</span>
                </Grid>
              </Tooltip>
            )}
          </>
        }
        disableGutters
        disablePadding
        disableMargin
      />
    );
  };

  const renderTagBadges = () => {
    const tag = fields.tag.value as Job["tag"];
    const values = tag ? tag.split(",") : [];

    return renderBadges({
      title: fields.tag.label,
      values,
      boxBadgeWidth: 110,
      boxContainerMax: Number.parseInt(String(tagContainerBounds.width), 10),
      className: classes.simpleTagBadge,
    });
  };

  return (
    <Grid
      container
      direction="column"
      key={task?.id}
      wrap={wrapLines ? "wrap" : "nowrap"}
      className={classes.content}
    >
      <Grid container item xs={12} rowGap="24px" spacing={1}>
        <Grid item xs={6}>
          {renderSimpleSection("orderReference")}
        </Grid>

        <Grid item xs={6}>
          {renderSimpleSection("customer")}
        </Grid>

        <Grid ref={tagContainerRef} item xs={12} md={6}>
          {renderTagBadges()}
        </Grid>
      </Grid>

      <Divider sx={{ marginTop: "18px", marginBottom: "18px" }} />

      <Grid
        item
        xs={12}
        container
        direction="row"
        justifyContent="space-between"
        spacing={1}
        rowGap="24px"
      >
        <Grid item xs={12} md={6}>
          {renderSimpleSection("taskType")}
        </Grid>

        <Grid item xs={12} md={6}>
          {renderSimpleSection("time")}
        </Grid>

        <Grid item xs={12} md={6} alignItems="start">
          {renderSimpleSection("leader")}
        </Grid>

        <Grid item xs={12} md={6} alignItems="start">
          {renderSimpleSection("staffs")}
        </Grid>

        <Grid item xs={12} md={6} alignItems="start">
          {renderSimpleSection("fleets")}
        </Grid>

        <Grid item xs={12} md={6} alignItems="start">
          {renderSimpleSection("storageBoxes")}
        </Grid>
      </Grid>
    </Grid>
  );
};
